// Pure edit rules for the quote drawer (#1559): the edit fields, the Save
// payload, template loading and the Guest/Qty/Flat toggle. Unit-tested in
// quote-drawer-logic.test.ts.
import type { QuoteRow, UpdateQuoteInput } from "@/hooks/useQuotes";
import type { LineItemDraft } from "@/components/quotes/QuoteLineItemRow";
import { lineTotal } from "@/lib/quote-totals";
import { effectiveServiceType, fulfillmentTypeFor, isPickup } from "@/types/quotes";

/** A line in the drawer's draft; `key` is client-only, added by useLineItemDrafts. */
export type DrawerLine = LineItemDraft & { key?: string };

export interface DrawerFields {
  serviceType: string;
  eventTypeId: string;
  discount: number;
  delivery: number;
  /** Square-synced only (#828); shown, never edited. */
  serviceCharge: number;
  gratuityOn: boolean;
  gratuityPercent: number;
  depositPercent: number;
  notes: string;
  internalNotes: string;
  lines: DrawerLine[];
}

export interface DrawerDefaults {
  defaultGratuityPct?: number;
  defaultDepositPct?: number;
}

export type UnitMode = "guest" | "qty" | "flat";

const num = (x: unknown) => Number(x) || 0;

/** The edit fields for a saved quote, with per-person lines sized to its guests. */
export function fieldsFromQuote(q: QuoteRow, defaults: DrawerDefaults = {}): DrawerFields {
  const guests = num(q.guest_count);
  const savedGratuity = num(q.gratuity_percent);
  return {
    // A pre-#873 /order pickup is drop_off + fulfillment_type 'pickup'; open it as Pickup.
    serviceType: effectiveServiceType(q.service_type, q.fulfillment_type),
    eventTypeId: q.event_type_id ?? "",
    discount: num(q.discount_amount),
    delivery: num(q.delivery_amount),
    serviceCharge: num(q.service_charge_amount),
    gratuityOn: savedGratuity > 0,
    gratuityPercent: savedGratuity > 0 ? savedGratuity : 0,
    depositPercent: q.deposit_percent != null ? num(q.deposit_percent) : (defaults.defaultDepositPct ?? 20),
    notes: q.notes ?? "",
    internalNotes: q.internal_notes ?? "",
    lines: (q.line_items ?? []).map((li) => {
      const unitPrice = num(li.unit_price);
      const perPerson = li.pricing_type === "per_person";
      const quantity = perPerson ? guests : num(li.quantity);
      return {
        menu_item_id: li.menu_item_id ?? undefined,
        package_id: li.package_id ?? undefined,
        name: li.name,
        description: li.description ?? "",
        pricing_type: li.pricing_type,
        unit_price: unitPrice,
        quantity,
        line_total: perPerson ? lineTotal(unitPrice, guests) : num(li.line_total),
      };
    }),
  };
}

const stripKeys = (f: DrawerFields) => ({ ...f, lines: f.lines.map(({ key: _key, ...li }) => li) });

/** True when two drafts hold the same values; line keys don't count. */
export function draftEquals(a: DrawerFields, b: DrawerFields): boolean {
  return JSON.stringify(stripKeys(a)) === JSON.stringify(stripKeys(b));
}

/** The gratuity percent a Save writes: null when off or not positive (#982). */
function savedGratuity(f: DrawerFields): number | null {
  if (!f.gratuityOn) return null;
  const pct = Math.min(100, Math.max(0, num(f.gratuityPercent)));
  return pct > 0 ? pct : null;
}

/**
 * The drawer's Save payload. It never names the event-owned fields (client,
 * contact, date, time, guests, driver): Edit details and Crew write those, so
 * a Save can't push stale event values back through the #1428 sync.
 */
export function buildSavePayload(latest: QuoteRow, f: DrawerFields): Omit<UpdateQuoteInput, "id"> {
  const pickup = isPickup(f.serviceType);
  return {
    service_type: f.serviceType,
    // Keep fulfillment_type in step with service_type, the source of truth (#873).
    fulfillment_type: fulfillmentTypeFor(f.serviceType),
    event_type_id: f.eventTypeId || null,
    // An update must send null to clear a column (#954).
    notes: f.notes.trim() || null,
    internal_notes: f.internalNotes.trim() || null,
    discount_amount: f.discount,
    // A pickup has no delivery; the typed fee stays in the draft (#818).
    delivery_amount: pickup ? 0 : f.delivery,
    ...(pickup ? { event_address: null } : {}),
    service_charge_amount: num(latest.service_charge_amount),
    gratuity_percent: savedGratuity(f),
    // useUpdateQuote nulls both when missing, so they are always sent.
    deposit_percent: f.depositPercent,
    payment_due_date: latest.payment_due_date ?? latest.event_date ?? undefined,
    tax_rate: 13,
    // `key` is client-only; it isn't a quote_line_items column.
    line_items: f.lines.map(({ key: _key, ...li }) => ({
      ...li,
      menu_item_id: li.menu_item_id ?? null,
      package_id: li.package_id ?? null,
      sort_order: 0,
    })),
  };
}

export function unitModeOf(pricingType: string): UnitMode {
  if (pricingType === "per_person") return "guest";
  if (pricingType === "flat") return "flat";
  return "qty";
}

/** The Guest / Qty / Flat toggle on an edit row. */
export function applyUnitMode(line: DrawerLine, mode: UnitMode, guests: number): DrawerLine {
  const current = unitModeOf(line.pricing_type);
  if (mode === current) return line;
  let next: DrawerLine;
  if (mode === "guest") next = { ...line, pricing_type: "per_person", quantity: guests };
  else if (mode === "flat") next = { ...line, pricing_type: "flat", quantity: 1 };
  else next = { ...line, pricing_type: "custom", quantity: Math.max(1, num(line.quantity)) };
  return { ...next, line_total: lineTotal(next.unit_price, next.quantity) };
}

/** The brand's default gratuity percent; 0 when missing or not positive (#982). */
export function gratuityDefault(settings: Record<string, string> | null | undefined): number {
  const pct = parseFloat(settings?.default_gratuity_percent ?? "");
  return Number.isFinite(pct) && pct > 0 ? pct : 0;
}
